import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Property, Auction } from '../../types';
import { Link } from 'react-router-dom';
import { REGIONS } from '../../data/mockData';

// Fix Leaflet default icon issue - use CDN URLs for reliability
const DefaultIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Helper function to get coordinates from city/region if lat/lng not available
function getPropertyCoordinates(property: Property | Auction): { lat: number; lng: number } | null {
  // If property has explicit coordinates, use them
  if (property.location.lat && property.location.lng) {
    return { lat: property.location.lat, lng: property.location.lng };
  }
  
  // Try to find coordinates from city/region
  const cityName = property.location.city;
  const regionName = property.location.region;
  
  for (const region of REGIONS) {
    // Check if region matches
    if (region.name === regionName) {
      // Try to find the city
      const city = region.cities.find(c => c.name === cityName);
      if (city?.lat && city?.lng) {
        return { lat: city.lat, lng: city.lng };
      }
      // If city not found but region has coordinates, use region center
      if (region.lat && region.lng) {
        return { lat: region.lat, lng: region.lng };
      }
    }
    // Also check if city matches in any region
    const cityInAnyRegion = region.cities.find(c => c.name === cityName);
    if (cityInAnyRegion?.lat && cityInAnyRegion?.lng) {
      return { lat: cityInAnyRegion.lat, lng: cityInAnyRegion.lng };
    }
  }
  
  return null;
}

interface OpenStreetMapProps {
  properties: (Property | Auction)[];
  center?: [number, number];
  zoom?: number;
  height?: string;
  onBoundsChange?: (bounds: any) => void;
  selectedRegion?: string;
  focusLocation?: { lat: number; lng: number; zoom?: number };
}

// Component to fit bounds to all properties
function BoundsFitter({ properties, selectedRegion, focusLocation }: { properties: (Property | Auction)[], selectedRegion?: string, focusLocation?: { lat: number; lng: number; zoom?: number } }) {
  const map = useMap();
  
  useEffect(() => {
    // Priority: focusLocation > properties with coords > default Serbia view
    if (focusLocation && focusLocation.lat && focusLocation.lng) {
      // Focus on the selected location (region or subregion)
      map.setView([focusLocation.lat, focusLocation.lng], focusLocation.zoom || 11);
      return;
    }
    
    const propertiesWithCoords = properties.filter(p => p.location.lat && p.location.lng);
    
    if (propertiesWithCoords.length > 0) {
      const bounds = L.latLngBounds(
        propertiesWithCoords.map(p => [p.location.lat!, p.location.lng!] as [number, number])
      );
      // Add some padding
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    } else if (selectedRegion) {
      // If no properties with coords but region selected, focus on Serbia
      map.setView([44.0165, 21.0059], 7);
    }
  }, [properties, map, selectedRegion, focusLocation]);
  
  return null;
}

export function OpenStreetMap({ 
  properties, 
  center = [44.0165, 21.0059], 
  zoom = 7, 
  height = '500px',
  selectedRegion,
  focusLocation 
}: OpenStreetMapProps) {
  // Serbia center: 44.0165, 21.0059
  
  // Get properties with coordinates (either explicit or from city/region lookup)
  const propertiesWithCoords = useMemo(() => {
    return properties
      .map(p => {
        const coords = getPropertyCoordinates(p);
        if (coords) {
          return { ...p, _resolvedLat: coords.lat, _resolvedLng: coords.lng };
        }
        return null;
      })
      .filter((p): p is (Property | Auction) & { _resolvedLat: number; _resolvedLng: number } => p !== null);
  }, [properties]);
  
  return (
    <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} touchZoom={true} dragging={true} style={{ height, width: '100%', zIndex: 1, borderRadius: '0.5rem' }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        subdomains="abcd"
        maxZoom={20}
      />
      <BoundsFitter properties={properties} selectedRegion={selectedRegion} focusLocation={focusLocation} />
      {propertiesWithCoords.map((property) => {
        // Odredi ispravnu putanju na osnovu tipa oglasa
        const linkPath = property.adKind === 'Aukcija'
          ? `/aukcije/${property.id}`
          : property.adKind === 'Potražnja'
          ? `/potraznja/${property.id}`
          : `/ponuda/${property.id}`;

        const isAuction = property.adKind === 'Aukcija';
        const displayPrice = isAuction
          ? (property as Auction).currentPrice
          : property.price;

        return (
          <Marker key={property.id} position={[property._resolvedLat, property._resolvedLng]}>
            <Popup>
              <div className="min-w-[200px]">
                <h3 className="font-bold text-sm mb-1">{property.title}</h3>
                <p className="text-amber-600 font-bold mb-2">
                  {displayPrice?.toLocaleString()} {property.currency}
                  {isAuction && <span className="text-xs text-slate-500 ml-1">(trenutna cena)</span>}
                </p>
                <div className="text-xs text-gray-600 mb-2">
                  {property.location.city}, {property.location.region}
                </div>
                <Link to={linkPath} className="block w-full text-center bg-amber-500 text-white py-1 px-2 rounded text-xs hover:bg-amber-600 transition-colors">
                  Pogledaj oglas
                </Link>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}
